export type VehicleFilterState = {
  searchTerm: string;
  selectedBrand: string;
  selectedModel: string;
  selectedFuels: string[];
  selectedTransmissions: string[];
  selectedType: string;
  selectedCategory: string;
  selectedColors: string[];
  selectedOptionals: string[];
  selectedStores: string[];
  priceRange: [number, number];
  yearRange: [number, number];
  mileageRange: [number, number];
};

export type FilterRanges = {
  minPrice: number;
  maxPrice: number;
  minYear: number;
  maxYear: number;
  minMileage: number;
  maxMileage: number;
};

export function buildDefaultFilters(
  ranges: FilterRanges,
  searchParams?: URLSearchParams
): VehicleFilterState {
  const params = searchParams ?? new URLSearchParams();

  const state: VehicleFilterState = {
    searchTerm: params.get('search') || '',
    selectedBrand: params.get('brand') || 'all',
    selectedModel: params.get('model') || 'all',
    selectedFuels: params.get('fuel') ? [params.get('fuel')!] : [],
    selectedTransmissions: params.get('transmission') ? [params.get('transmission')!] : [],
    selectedType: params.get('type') || 'all',
    selectedCategory: params.get('category') || 'all',
    selectedColors: params.get('color') ? [params.get('color')!] : [],
    selectedOptionals: params.get('optionals')
      ? params.get('optionals')!.split(',').filter(Boolean)
      : [],
    selectedStores: params.get('store') ? [params.get('store')!] : [],
    priceRange: [ranges.minPrice, ranges.maxPrice],
    yearRange: [ranges.minYear, ranges.maxYear],
    mileageRange: [ranges.minMileage, ranges.maxMileage],
  };

  const minYearParam = params.get('minYear');
  const maxYearParam = params.get('maxYear');
  if (minYearParam && maxYearParam) {
    const minYear = parseInt(minYearParam, 10);
    const maxYear = parseInt(maxYearParam, 10);
    if (!isNaN(minYear) && !isNaN(maxYear)) {
      state.yearRange = [minYear, maxYear];
    }
  }

  const minPriceParam = params.get('minPrice');
  const maxPriceParam = params.get('maxPrice');
  if (minPriceParam && maxPriceParam) {
    const minPrice = parseInt(minPriceParam, 10);
    const maxPrice = parseInt(maxPriceParam, 10);
    if (!isNaN(minPrice) && !isNaN(maxPrice)) {
      state.priceRange = [minPrice, maxPrice];
    }
  }

  const minMileageParam = params.get('minMileage');
  const maxMileageParam = params.get('maxMileage');
  if (minMileageParam && maxMileageParam) {
    const minMileage = parseInt(minMileageParam, 10);
    const maxMileage = parseInt(maxMileageParam, 10);
    if (!isNaN(minMileage) && !isNaN(maxMileage)) {
      state.mileageRange = [minMileage, maxMileage];
    }
  }

  return state;
}

export function filtersToSearchParams(filters: VehicleFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.searchTerm.trim()) params.set('search', filters.searchTerm.trim());
  if (filters.selectedBrand !== 'all') params.set('brand', filters.selectedBrand);
  if (filters.selectedModel !== 'all') params.set('model', filters.selectedModel);
  if (filters.selectedType !== 'all') params.set('type', filters.selectedType);
  if (filters.selectedCategory !== 'all') params.set('category', filters.selectedCategory);
  if (filters.selectedFuels.length === 1) params.set('fuel', filters.selectedFuels[0]);
  if (filters.selectedTransmissions.length === 1) {
    params.set('transmission', filters.selectedTransmissions[0]);
  }
  if (filters.selectedColors.length === 1) params.set('color', filters.selectedColors[0]);
  if (filters.selectedStores.length === 1) params.set('store', filters.selectedStores[0]);
  if (filters.selectedOptionals.length > 0) {
    params.set('optionals', filters.selectedOptionals.join(','));
  }
  params.set('minPrice', String(filters.priceRange[0]));
  params.set('maxPrice', String(filters.priceRange[1]));
  params.set('minYear', String(filters.yearRange[0]));
  params.set('maxYear', String(filters.yearRange[1]));
  params.set('minMileage', String(filters.mileageRange[0]));
  params.set('maxMileage', String(filters.mileageRange[1]));
  return params;
}

export function hasActiveFilters(filters: VehicleFilterState, ranges: FilterRanges): boolean {
  return (
    !!filters.searchTerm ||
    filters.selectedBrand !== 'all' ||
    filters.selectedModel !== 'all' ||
    filters.selectedFuels.length > 0 ||
    filters.selectedTransmissions.length > 0 ||
    filters.selectedType !== 'all' ||
    filters.selectedCategory !== 'all' ||
    filters.selectedColors.length > 0 ||
    filters.selectedOptionals.length > 0 ||
    filters.selectedStores.length > 0 ||
    filters.priceRange[0] > ranges.minPrice ||
    filters.priceRange[1] < ranges.maxPrice ||
    filters.yearRange[0] > ranges.minYear ||
    filters.yearRange[1] < ranges.maxYear ||
    filters.mileageRange[0] > ranges.minMileage ||
    filters.mileageRange[1] < ranges.maxMileage
  );
}

export function hasAdvancedFilters(filters: VehicleFilterState, ranges: FilterRanges): boolean {
  return (
    !!filters.searchTerm ||
    filters.selectedFuels.length > 0 ||
    filters.selectedTransmissions.length > 0 ||
    filters.selectedCategory !== 'all' ||
    filters.selectedColors.length > 0 ||
    filters.selectedOptionals.length > 0 ||
    filters.selectedStores.length > 0 ||
    filters.priceRange[0] > ranges.minPrice ||
    filters.priceRange[1] < ranges.maxPrice ||
    filters.yearRange[0] > ranges.minYear ||
    filters.yearRange[1] < ranges.maxYear ||
    filters.mileageRange[0] > ranges.minMileage ||
    filters.mileageRange[1] < ranges.maxMileage
  );
}

export function filterVehicles(
  contextVehicles: any[],
  filters: VehicleFilterState,
  searchParams: URLSearchParams,
  ranges: FilterRanges
): any[] {
  const idsParam = searchParams.get('ids');
  if (idsParam) {
    const vehicleIds = idsParam.split(',');
    return contextVehicles.filter((vehicle) => vehicleIds.includes(vehicle.id.toString()));
  }

  const urlFuel = searchParams.get('fuel');
  const urlTransmission = searchParams.get('transmission');
  const urlColor = searchParams.get('color');

  const activeFuels = urlFuel ? [urlFuel] : filters.selectedFuels;
  const activeTransmissions = urlTransmission ? [urlTransmission] : filters.selectedTransmissions;
  const activeColors = urlColor ? [urlColor] : filters.selectedColors;

  return contextVehicles.filter((vehicle) => {
    const matchesSearch =
      filters.searchTerm === '' ||
      `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
        .toLowerCase()
        .includes(filters.searchTerm.toLowerCase());

    const matchesBrand = filters.selectedBrand === 'all' || vehicle.brand === filters.selectedBrand;
    const matchesModel = filters.selectedModel === 'all' || vehicle.model === filters.selectedModel;
    const matchesFuels = activeFuels.length === 0 || activeFuels.includes(vehicle.fuel);
    const matchesTransmissions =
      activeTransmissions.length === 0 || activeTransmissions.includes(vehicle.transmission);
    const matchesType =
      filters.selectedType === 'all' ||
      (filters.selectedType === 'zerokm' && (vehicle.isZeroKm || vehicle.mileage == 0)) ||
      (filters.selectedType === 'seminovo' && !vehicle.isZeroKm && vehicle.mileage > 0);
    const matchesCategory =
      filters.selectedCategory === 'all' || vehicle.type.toString() === filters.selectedCategory;
    const matchesColors = activeColors.length === 0 || activeColors.includes(vehicle.color);
    const matchesOptionals =
      filters.selectedOptionals.length === 0 ||
      filters.selectedOptionals.every((optional) => vehicle.optionals?.includes(optional));
    const matchesStores =
      filters.selectedStores.length === 0 ||
      (vehicle.store && filters.selectedStores.includes(vehicle.store));
    const matchesPrice =
      vehicle.price >= filters.priceRange[0] && vehicle.price <= filters.priceRange[1];
    const matchesYear =
      filters.yearRange[0] === filters.yearRange[1]
        ? vehicle.modelYear === filters.yearRange[0]
        : vehicle.manufactureYear >= filters.yearRange[0] &&
          vehicle.modelYear <= filters.yearRange[1];
    const matchesMileage =
      vehicle.mileage >= filters.mileageRange[0] && vehicle.mileage <= filters.mileageRange[1];

    return (
      matchesSearch &&
      matchesBrand &&
      matchesModel &&
      matchesFuels &&
      matchesTransmissions &&
      matchesType &&
      matchesCategory &&
      matchesColors &&
      matchesOptionals &&
      matchesStores &&
      matchesPrice &&
      matchesYear &&
      matchesMileage
    );
  });
}

export function sortVehicles(vehicles: any[], sortBy: string): any[] {
  const sorted = [...vehicles];
  switch (sortBy) {
    case 'price-asc':
      sorted.sort((a, b) => a.price - b.price);
      break;
    case 'price-desc':
      sorted.sort((a, b) => b.price - a.price);
      break;
    case 'year-desc':
      sorted.sort((a, b) => b.modelYear - a.modelYear);
      break;
    case 'year-asc':
      sorted.sort((a, b) => a.modelYear - b.modelYear);
      break;
    case 'mileage-asc':
      sorted.sort((a, b) => a.mileage - b.mileage);
      break;
    case 'brand':
      sorted.sort((a, b) => a.brand.localeCompare(b.brand));
      break;
    default:
      break;
  }
  return sorted;
}
